/**
 * Applications — where the EWM side-car lands, organised by industry.
 *
 * These are applicability statements, not performance claims. Each entry says
 * what the industry constraint is, what the side-car provides against it, and
 * which measured scenarios in the research portfolio back it up. No industry
 * deployment numbers are published here because none have been run.
 *
 * `scenarios` holds ids from data/scenarios.js — the measured work that would
 * be deployed in that vertical.
 */

export const APPLICATIONS = [
  {
    id: 'it-enterprise',
    icon: '🖥️',
    name: 'IT & Enterprise Software',
    tagline: 'Long-running agents and several models, with a state you can inspect.',
    constraint:
      'Agents now run for hours or days across many tools, and more than one model is usually in the loop. Nobody can answer what the session has retained, what it dropped, or which model saw which state.',
    why:
      'Context is re-sent every turn, so cost and latency rise with session length, and anything removed to fit the window is removed heuristically. The system has no memory it can describe.',
    provides: [
      'Session state held outside the model, so it survives a restart instead of being re-sent.',
      'Departed, retained and new computed per turn — a precise answer to what this step changed.',
      'One state spanning several models, so the comparison between them is measurable rather than anecdotal.',
      'The chosen model and its confidence recorded as part of the state, so routing decisions are auditable too.',
    ],
    scenarios: ['multi-model', 'ingest-materialize', 'operational-graph'],
    notes: [
      'Restart behaviour is demonstrated rather than asserted: the state is picked up from the persistent stack, and recovery is a pop rather than a replay.',
    ],
  },
  {
    id: 'defence',
    icon: '🛡️',
    name: 'Defence & National Security',
    tagline: 'Inside the boundary, with a record an operator can read.',
    constraint:
      'Deployments sit in disconnected or restricted networks, hardware is fixed in advance, and every automated decision has to be reconstructable later by someone who was not there.',
    why:
      'Systems that call out to a hosted service are not options, and a memory that cannot be inspected is not an acceptable component in an accredited environment.',
    provides: [
      'The whole layer runs inside the customer boundary, with no outbound dependency to operate.',
      'An append-only, content-addressed record: every state has a key, and equal states share one.',
      'Materialized memory — the state lowers back to an ordered, readable token collection, so the memory is inspectable rather than opaque.',
      'One apparatus across sensing, action and prediction streams, which keeps the accredited surface small.',
    ],
    scenarios: ['operational-graph', 'heterogeneous-modalities', 'scene-sidecar'],
    notes: [
      'Accreditation, handling rules and export classification are the customer framework to satisfy; nothing here claims to meet a specific standard.',
      'The published results are research demonstrations in controlled conditions, not field results.',
    ],
  },
  {
    id: 'healthcare',
    icon: '🏥',
    name: 'Healthcare',
    tagline: 'Clinical documents triaged where the record already lives.',
    constraint:
      'Records cannot leave the premises, and any statement a system produces becomes part of a clinical record somebody is accountable for.',
    why:
      'Report triage and document matching are exactly the tasks that tempt teams towards a hosted model, which the data rules usually forbid. A system that cannot show which findings it used cannot be reviewed.',
    provides: [
      'Report triage and document matching inside the customer boundary.',
      'A successive-report view: what is new in this report, what carried over, and what has gone — computed rather than summarised.',
      'An audit trail by construction, so the evidence behind a match can be produced later.',
      'Numeric reference ranges and finding cards held as state, so the comparison is deterministic.',
    ],
    scenarios: ['scene-sidecar', 'heterogeneous-modalities', 'ingest-materialize'],
    notes: [
      'Clinical and diagnostic outputs are research demonstrations for partner evaluation. They are not a medical device and do not replace clinician review.',
    ],
  },
  {
    id: 'insurance',
    icon: '📋',
    name: 'Insurance',
    tagline: 'A case that is revisited for months, with a memory that keeps up.',
    constraint:
      'A claim accumulates documents over a long period and is read by different people at different times, often to justify a decision made earlier.',
    why:
      'Summaries lose the detail that matters when a decision is challenged, and a similarity search cannot say what changed between two versions of the same submission.',
    provides: [
      'Case state held across submissions, so a later reader starts from the accumulated state rather than from scratch.',
      'Document-level novelty between submissions: what this version added, retained and dropped.',
      'Ordered restoration of the exact document state, so the basis of a decision is reproducible.',
      'The same apparatus reads correspondence, forms and image-based documents without separate pipelines.',
    ],
    scenarios: ['scene-sidecar', 'heterogeneous-modalities', 'ingest-materialize'],
    notes: [
      'Document extraction quality remains a host-side question; the side-car guarantees the structure and provenance of the state, not the perceptual quality of a scanned page.',
    ],
  },
  {
    id: 'banking-financial',
    icon: '🏦',
    name: 'Banking & Financial Services',
    tagline: 'Model risk governance, met with versioned state.',
    constraint:
      'Data residency rules and model risk governance both apply. A change in behaviour has to be explainable, attributable and reproducible after the fact.',
    why:
      'Retraining or fine-tuning to add knowledge destroys the ability to say what the system knew on a given date, and re-sending a long context is both expensive and unverifiable.',
    provides: [
      'State addressed by content, so a past state is named by a key and remains valid forever.',
      'The host model is never retrained, so its governance approval is not invalidated by adding memory.',
      'Departed, retained and new over successive states — a change record rather than a change narrative.',
      'The layer runs inside the institution boundary, on infrastructure already approved.',
    ],
    scenarios: ['operational-graph', 'multi-model', 'ingest-materialize'],
    notes: [
      'Governance approval of a deployment is the institution process; the contribution here is that the memory layer is separately versioned and inspectable.',
    ],
  },
  {
    id: 'legal',
    icon: '⚖️',
    name: 'Legal',
    tagline: 'A reconstructable record of what a system was given and what it kept.',
    constraint:
      'Privileged material cannot leave the firm, and a conclusion has to be traceable to the exact documents behind it, sometimes years later.',
    why:
      'Review work is repetitive and long, and the reasoning behind a flag is exactly what a summary discards. Retrieval shows what is similar, not what is new in this version of a document.',
    provides: [
      'A state per matter that persists across review sessions.',
      'Version-to-version change detection on documents: added, kept and removed passages.',
      'Ordered restoration, so the exact content behind a conclusion can be reproduced.',
      'Immutability by construction, which suits a record that must not be silently altered.',
    ],
    scenarios: ['scene-sidecar', 'ingest-materialize', 'heterogeneous-modalities'],
    notes: [
      'Nothing here is legal advice or a compliance certification; the layer produces a checkable record that a professional reviews.',
    ],
  },
  {
    id: 'education-research',
    icon: '🎓',
    name: 'Education & Research',
    tagline: 'Reproducible state, so a second lab can reach the same result.',
    constraint:
      'Research lives or dies on reproduction: a different team, on different hardware, has to be able to reach the same state from the same inputs.',
    why:
      'Model behaviour drifts with versions and sampling, so a result that depends on a model call is hard to pin down. Most memory layers are stores whose contents nobody can verify.',
    provides: [
      'Idempotent and immutable states: the same input yields the same state and the same key, every time.',
      'A measured interface that is stable across model versions, so the result is not tied to one checkpoint.',
      'The whole loop is published as runnable notebooks, including the cases where the honest result is weaker than expected.',
      'Teaching material that runs the same apparatus across vision, driving, generation and language — one mechanism, many subjects.',
    ],
    scenarios: ['jepa-cooperation', 'ingest-materialize', 'generative-latents', 'multi-model'],
    notes: [
      'The published results include their own negative findings, and the limits are stated next to each figure.',
    ],
  },
];
